import { Box, Link, Stack, Typography, styled } from "~/common/mui";
import type { CMSProductCarouselProps, ProductCarouselItemDTO } from "./types";

export const CMSProductCarousel = ({ title, products, className }: CMSProductCarouselProps) => {
  return (
    <Stack className={className} spacing={2}>
      {title && title.length > 0 && <Typography variant="headlineSmall">{title}</Typography>}
      <CarouselRow>
        {products.map((product) => (
          <ProductCard key={product.code} product={product} />
        ))}
      </CarouselRow>
    </Stack>
  );
};

const ProductCard = ({ product }: { product: ProductCarouselItemDTO }) => {
  const content = (
    <Card>
      {product.imageUrl && <ProductImage src={product.imageUrl} alt={product.name ?? product.code} loading="lazy" />}
      <Stack spacing={0.5} sx={{ padding: (theme) => theme.spacing(1.5) }}>
        <ProductName variant="bodyLarge">{product.name ?? product.code}</ProductName>
        {product.price?.formattedValue && <Typography variant="titleMedium">{product.price.formattedValue}</Typography>}
      </Stack>
    </Card>
  );

  if (product.url && product.url.length > 0) {
    return (
      <Link href={product.url} underline="none" color="inherit" sx={{ flexShrink: 0 }}>
        {content}
      </Link>
    );
  }
  return <Box sx={{ flexShrink: 0 }}>{content}</Box>;
};

const CarouselRow = styled(Stack)(({ theme }) => ({
  flexDirection: "row",
  gap: theme.spacing(2),
  overflowX: "auto",
  paddingBottom: theme.spacing(1),
  scrollSnapType: "x mandatory",
  "& > *": {
    scrollSnapAlign: "start",
  },
}));

const Card = styled(Stack)(({ theme }) => ({
  width: theme.spacing(28),
  minWidth: theme.spacing(28),
  borderRadius: theme.spacing(2),
  overflow: "hidden",
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  height: "100%",
}));

const ProductImage = styled("img")(({ theme }) => ({
  width: "100%",
  height: theme.spacing(24),
  objectFit: "contain",
  backgroundColor: theme.palette.background.default,
}));

const ProductName = styled(Typography)(({ theme }) => ({
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
  minHeight: theme.typography.bodyLarge.lineHeight ? `calc(${theme.typography.bodyLarge.lineHeight} * 2em)` : undefined,
}));
